import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Radio,
  Target,
  Users,
  TrendingUp,
  Clock,
  CheckCircle,
  BarChart3,
  Plus,
  Loader2,
  Briefcase,
  ArrowRight,
  Zap,
  Shield,
  Globe,
  Award,
  DollarSign,
  Rocket,
  ChevronRight
} from 'lucide-react';
import {
  getTelekomConfig,
  addTelekomEmployee,
  getTelekomEmployees,
  getTelekomKPIs,
  updateEmployeeStatus
} from '../services/api';
import { toast } from 'sonner';

const TM = '#E20074';

const TelekomPilot = () => {
  const [config, setConfig] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newEmp, setNewEmp] = useState({
    name: '', position: '', cluster: '', kompetenzen: [], years_experience: 0, work_model: 'hybrid'
  });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [cfgRes, empRes, kpiRes] = await Promise.all([
        getTelekomConfig(),
        getTelekomEmployees().catch(() => ({ data: [] })),
        getTelekomKPIs().catch(() => ({ data: null }))
      ]);
      setConfig(cfgRes.data);
      setEmployees(empRes.data);
      setKpis(kpiRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleAddEmployee = async () => {
    if (!newEmp.name || !newEmp.cluster) { toast.error('Name und Cluster erforderlich'); return; }
    setAdding(true);
    try {
      const res = await addTelekomEmployee(newEmp);
      setEmployees([...employees, res.data]);
      setNewEmp({ name: '', position: '', cluster: '', kompetenzen: [], years_experience: 0, work_model: 'hybrid' });
      setShowAddEmployee(false);
      toast.success('Mitarbeiter hinzugefügt');
      const kpiRes = await getTelekomKPIs();
      setKpis(kpiRes.data);
    } catch (e) { toast.error('Fehler'); }
    finally { setAdding(false); }
  };

  const toggleKompetenz = (k) => {
    setNewEmp(prev => ({
      ...prev,
      kompetenzen: prev.kompetenzen.includes(k)
        ? prev.kompetenzen.filter(x => x !== k)
        : [...prev.kompetenzen, k]
    }));
  };

  const selectedCluster = config?.clusters?.find(c => c.key === newEmp.cluster);
  const cfg = config?.config || {};

  const getStatusBadge = (status) => {
    const map = {
      pending: { label: 'Ausstehend', cls: 'bg-gray-100 text-gray-700' },
      profiling: { label: 'Profiling', cls: 'bg-blue-100 text-blue-700' },
      matching: { label: 'Matching', cls: 'bg-yellow-100 text-yellow-700' },
      interviewing: { label: 'Interview', cls: 'bg-purple-100 text-purple-700' },
      placed: { label: 'Platziert', cls: 'bg-green-100 text-green-700' }
    };
    const s = map[status] || map.pending;
    return <Badge className={s.cls}>{s.label}</Badge>;
  };

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" style={{ color: TM }} /></div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="kompetenz-dna-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${TM}15` }}>
            <Radio className="w-7 h-7" style={{ color: TM }} />
          </div>
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground">Kompetenz DNA-Arbeitsmarkt Drehscheibe</h1>
            <p className="text-muted-foreground">Kompetenz-DNA Outplacement-Programm</p>
          </div>
        </div>

        <Tabs defaultValue="pilot" className="space-y-6">
          <TabsList className="grid grid-cols-4 w-full max-w-2xl">
            <TabsTrigger value="pilot">Pilotphase</TabsTrigger>
            <TabsTrigger value="kpis">KPI-Dashboard</TabsTrigger>
            <TabsTrigger value="employees">Mitarbeitende</TabsTrigger>
            <TabsTrigger value="rollout">Rollout</TabsTrigger>
          </TabsList>

          {/* ===== PILOTPHASE ===== */}
          <TabsContent value="pilot" className="space-y-6">
            {/* Zielsetzung */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5" style={{ color: TM }} />
                  Zielsetzung
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {[
                    'Übersetzung interner Funktionsstrukturen in marktfähige Kompetenz-DNA-Profile',
                    'Erhöhung der externen Vermittlungsquote',
                    'Verkürzung der durchschnittlichen Neuplatzierungsdauer',
                    'Transparenz über Transferfähigkeit von Mitarbeitenden',
                    'Aufbau einer skalierbaren Konzernlösung'
                  ].map((z, i) => (
                    <div key={i} className="flex items-start gap-2 p-3 rounded-lg border border-border">
                      <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: TM }} />
                      <span className="text-sm">{z}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Umfang */}
            <Card style={{ borderColor: `${TM}30` }}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" style={{ color: TM }} />
                  Pilotphase – Strukturierter Einstieg
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 rounded-lg text-center" style={{ background: `${TM}08` }}>
                    <p className="text-2xl font-bold" style={{ color: TM }}>{cfg.min_employees}–{cfg.max_employees}</p>
                    <p className="text-sm text-muted-foreground">Mitarbeitende</p>
                  </div>
                  <div className="p-4 rounded-lg text-center" style={{ background: `${TM}08` }}>
                    <p className="text-2xl font-bold" style={{ color: TM }}>{cfg.duration_months} Monate</p>
                    <p className="text-sm text-muted-foreground">Laufzeit</p>
                  </div>
                  <div className="p-4 rounded-lg text-center" style={{ background: `${TM}08` }}>
                    <p className="text-2xl font-bold" style={{ color: TM }}>1–2</p>
                    <p className="text-sm text-muted-foreground">Kompetenzcluster</p>
                  </div>
                  <div className="p-4 rounded-lg text-center" style={{ background: `${TM}08` }}>
                    <p className="text-2xl font-bold" style={{ color: TM }}>KPI</p>
                    <p className="text-sm text-muted-foreground">Erfolgsmessung</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Kompetenzcluster */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5" style={{ color: TM }} />
                  Kompetenzcluster
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  {config?.clusters?.map((cl, idx) => (
                    <div key={cl.key} className={`p-5 rounded-xl border-2 ${idx < 2 ? '' : 'border-dashed opacity-80'}`}
                      style={{ borderColor: idx < 2 ? TM : undefined }}>
                      <div className="flex items-center gap-2 mb-3">
                        <Badge style={{ background: idx < 2 ? TM : '#6b7280', color: 'white' }}>
                          Cluster {idx + 1}
                        </Badge>
                        {idx === 2 && <Badge variant="outline" className="text-xs">Optional</Badge>}
                      </div>
                      <h4 className="font-semibold mb-3">{cl.name}</h4>
                      <div className="space-y-1.5">
                        {cl.kompetenzen.map((k, ki) => (
                          <div key={ki} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <ChevronRight className="w-3 h-3" style={{ color: TM }} />
                            {k}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Technisches Vorgehen */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" style={{ color: TM }} />
                  Technisches Vorgehen
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {[
                    { icon: Target, text: 'Extraktion von Tätigkeiten statt Titel-Matching' },
                    { icon: Users, text: 'Aufbau multidimensionaler Kompetenz-DNA-Profile' },
                    { icon: TrendingUp, text: 'Prozentuales Matching gegen externen Markt' },
                    { icon: ArrowRight, text: 'Transferbranchen-Analyse' },
                    { icon: Globe, text: 'Arbeitsmodell-Matching (Remote/Hybrid/Vor Ort)' },
                    { icon: Briefcase, text: 'Bewerbungsoptimierung' }
                  ].map(({ icon: Icon, text }, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <Icon className="w-5 h-5 flex-shrink-0" style={{ color: TM }} />
                      <span className="text-sm">{text}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Argumentation */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" style={{ color: TM }} />
                  Argumentation für den Pilot
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    'Kontrollierte Komplexität durch Cluster-Auswahl',
                    'Messbarer Impact',
                    'Geteiltes Risiko durch Erfolgsmodell',
                    'Schnelle Implementierung ohne interne Systemintegration',
                    'Grundlage für datenbasierten Rollout'
                  ].map((a, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-border">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: TM }}>
                        <span className="text-white text-xs font-bold">{i + 1}</span>
                      </div>
                      <span className="text-sm">{a}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== KPI DASHBOARD ===== */}
          <TabsContent value="kpis" className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6 text-center">
                  <p className="text-3xl font-bold" style={{ color: TM }}>{kpis?.total_employees || 0}</p>
                  <p className="text-sm text-muted-foreground">Mitarbeitende gesamt</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <p className="text-3xl font-bold text-green-600">{kpis?.placed || 0}</p>
                  <p className="text-sm text-muted-foreground">Platziert</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <p className="text-3xl font-bold text-purple-600">{kpis?.in_progress || 0}</p>
                  <p className="text-sm text-muted-foreground">Im Interview</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <p className="text-3xl font-bold text-yellow-600">{kpis?.pending || 0}</p>
                  <p className="text-sm text-muted-foreground">Ausstehend</p>
                </CardContent>
              </Card>
            </div>

            {/* KPI Metrics */}
            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <CardHeader><CardTitle className="text-base">Vermittlungsquote</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex items-end gap-3 mb-2">
                    <span className="text-4xl font-bold" style={{ color: TM }}>{kpis?.placement_rate || 0}%</span>
                  </div>
                  <Progress value={kpis?.placement_rate || 0} className="h-3" />
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-base">Ø Match-Score</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex items-end gap-3 mb-2">
                    <span className="text-4xl font-bold" style={{ color: TM }}>{kpis?.avg_match_score || 0}%</span>
                  </div>
                  <Progress value={kpis?.avg_match_score || 0} className="h-3" />
                </CardContent>
              </Card>
            </div>

            {/* KPI Tracking Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" style={{ color: TM }} />
                  KPI-Tracking Metriken
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    { label: 'Vermittlungsquote', desc: 'Anteil platzierter Mitarbeitender' },
                    { label: 'Ø Zeit bis Neuplatzierung', desc: 'Durchschnittliche Dauer in Tagen' },
                    { label: 'Interview-Conversion-Rate', desc: 'Interviews die zur Platzierung führen' },
                    { label: 'Qualifizierte Matches/Person', desc: 'Anzahl passender Stellen pro MA' }
                  ].map((kpi, i) => (
                    <div key={i} className="p-4 rounded-lg bg-muted/50">
                      <p className="font-semibold text-sm">{kpi.label}</p>
                      <p className="text-xs text-muted-foreground mt-1">{kpi.desc}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Cluster Distribution */}
            {kpis?.cluster_distribution && Object.keys(kpis.cluster_distribution).length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-base">Cluster-Verteilung</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {config?.clusters?.map(cl => {
                      const count = kpis.cluster_distribution[cl.key] || 0;
                      const pct = kpis.total_employees > 0 ? Math.round(count / kpis.total_employees * 100) : 0;
                      return (
                        <div key={cl.key}>
                          <div className="flex justify-between text-sm mb-1">
                            <span>{cl.name}</span>
                            <span className="font-medium">{count} ({pct}%)</span>
                          </div>
                          <Progress value={pct} className="h-2" />
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ===== MITARBEITENDE ===== */}
          <TabsContent value="employees" className="space-y-6">
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground">{employees.length} Mitarbeitende erfasst</p>
              <Button onClick={() => setShowAddEmployee(true)} className="rounded-full" style={{ background: TM }} data-testid="add-employee-btn">
                <Plus className="w-4 h-4 mr-2" />
                Mitarbeiter hinzufügen
              </Button>
            </div>

            {showAddEmployee && (
              <Card style={{ borderColor: `${TM}30` }}>
                <CardHeader><CardTitle>Mitarbeiter hinzufügen</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Name *</Label>
                      <Input value={newEmp.name} onChange={e => setNewEmp({ ...newEmp, name: e.target.value })} placeholder="Max Mustermann" data-testid="emp-name-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Position</Label>
                      <Input value={newEmp.position} onChange={e => setNewEmp({ ...newEmp, position: e.target.value })} placeholder="Projektmanager" />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Cluster *</Label>
                      <Select value={newEmp.cluster} onValueChange={v => setNewEmp({ ...newEmp, cluster: v, kompetenzen: [] })}>
                        <SelectTrigger data-testid="emp-cluster-select"><SelectValue placeholder="Cluster wählen" /></SelectTrigger>
                        <SelectContent>
                          {config?.clusters?.map(c => (
                            <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Erfahrung (Jahre)</Label>
                      <Input type="number" min={0} value={newEmp.years_experience} onChange={e => setNewEmp({ ...newEmp, years_experience: parseInt(e.target.value) || 0 })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Arbeitsmodell</Label>
                      <Select value={newEmp.work_model} onValueChange={v => setNewEmp({ ...newEmp, work_model: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="remote">Remote</SelectItem>
                          <SelectItem value="hybrid">Hybrid</SelectItem>
                          <SelectItem value="onsite">Vor Ort</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {selectedCluster && (
                    <div className="space-y-2">
                      <Label>Kompetenzen</Label>
                      <div className="flex flex-wrap gap-2">
                        {selectedCluster.kompetenzen.map(k => (
                          <Badge key={k} variant={newEmp.kompetenzen.includes(k) ? 'default' : 'outline'}
                            className="cursor-pointer" style={newEmp.kompetenzen.includes(k) ? { background: TM } : {}}
                            onClick={() => toggleKompetenz(k)}>{k}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => setShowAddEmployee(false)} className="rounded-full">Abbrechen</Button>
                    <Button onClick={handleAddEmployee} disabled={adding || !newEmp.name || !newEmp.cluster} className="rounded-full flex-1" style={{ background: TM }} data-testid="submit-employee-btn">
                      {adding && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Hinzufügen
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {employees.length > 0 ? (
              <div className="grid gap-3">
                {employees.map(emp => (
                  <Card key={emp.employee_id} className="hover:shadow-sm transition-all" data-testid={`emp-${emp.employee_id}`}>
                    <CardContent className="py-4">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${TM}15` }}>
                          <span className="text-sm font-bold" style={{ color: TM }}>{emp.name?.charAt(0)}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold truncate">{emp.name}</p>
                          <p className="text-sm text-muted-foreground">{emp.position || 'Keine Position'} · {config?.clusters?.find(c => c.key === emp.cluster)?.name || emp.cluster}</p>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {emp.match_score > 0 && (
                            <span className="text-sm font-bold" style={{ color: TM }}>{emp.match_score}%</span>
                          )}
                          {getStatusBadge(emp.placement_status)}
                          <Badge variant="outline" className="text-xs capitalize">{emp.work_model}</Badge>
                        </div>
                      </div>
                      {emp.kompetenzen?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-3 ml-14">
                          {emp.kompetenzen.map((k, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">{k}</Badge>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : !showAddEmployee && (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <Users className="w-16 h-16 mx-auto mb-4" style={{ color: `${TM}40` }} />
                  <h3 className="text-xl font-semibold mb-2">Noch keine Mitarbeitenden</h3>
                  <p className="text-muted-foreground mb-4">Fügen Sie die ersten Pilot-Teilnehmer hinzu.</p>
                  <Button onClick={() => setShowAddEmployee(true)} className="rounded-full" style={{ background: TM }}>
                    <Plus className="w-4 h-4 mr-2" />
                    Ersten Mitarbeiter hinzufügen
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ===== ROLLOUT ===== */}
          <TabsContent value="rollout" className="space-y-6">
            {/* Voraussetzungen */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" style={{ color: TM }} />
                  Voraussetzungen für Rollout
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {['Nachweisbare KPI-Verbesserung', 'Dokumentierte Erfolgsfälle', 'Positive HR- und Managementbewertung'].map((v, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <div className="w-6 h-6 rounded-full border-2 flex items-center justify-center" style={{ borderColor: TM }}>
                        <span className="text-xs font-bold" style={{ color: TM }}>{i + 1}</span>
                      </div>
                      <span className="text-sm">{v}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Erweiterung */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Rocket className="w-5 h-5" style={{ color: TM }} />
                  Erweiterung
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    'Ausweitung auf weitere Kompetenzcluster',
                    'Konzernweite Lizenzierung',
                    'White-Label-Option',
                    'Integration in Restrukturierungsstrategie'
                  ].map((e, i) => (
                    <div key={i} className="flex items-center gap-2 p-3 rounded-lg border border-border">
                      <ArrowRight className="w-4 h-4 flex-shrink-0" style={{ color: TM }} />
                      <span className="text-sm">{e}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Strategischer Mehrwert */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="w-5 h-5" style={{ color: TM }} />
                  Strategischer Mehrwert im Rollout
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    'Reduzierung von Sozialplan- und Transferkosten',
                    'ESG- und Diversity-Positionierung',
                    'Aufbau einer konzernweiten Kompetenzlandkarte',
                    'Zukunftsfähige Infrastruktur für weitere Restrukturierungen'
                  ].map((m, i) => (
                    <div key={i} className="flex items-start gap-3 p-4 rounded-lg" style={{ background: `${TM}06` }}>
                      <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: TM }} />
                      <span className="text-sm font-medium">{m}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default TelekomPilot;
